import { notFound } from "@tanstack/react-router";
import {
  backendFetchResponse,
  parseResponse,
} from "@softmax/observatory-client/transport";
import type {
  PolicyVersionResolution,
  PolicyVersionRow,
  Schemas,
} from "@softmax/observatory-client/types";

import { getActualViewer } from "@/data/viewer";

function policyIdentity({ version }: { version: PolicyVersionRow }) {
  return {
    id: version.id,
    policy_id: version.policy_id,
    name: version.name,
    version: version.version,
    created_at: version.created_at,
    policy_created_at: version.policy_created_at,
    user_id: version.user_id,
    owner_coworld_name: version.owner_coworld_name ?? null,
    user:
      version.user === null
        ? null
        : { id: version.user.id, name: version.user.name ?? null },
  } satisfies PolicyVersionRow;
}

export async function resolvePolicyIdentity({
  parameters,
  allowHiddenVersion,
  signal,
}: {
  parameters: URLSearchParams;
  allowHiddenVersion: boolean;
  signal?: AbortSignal | undefined;
}) {
  const read = (credentials: RequestCredentials) =>
    backendFetchResponse(
      "/api/observatory",
      `/stats/policy-versions/resolve?${parameters}`,
      {
        credentials,
        signal: signal ?? null,
        headers: { "X-Use-Elevated-Privileges": "false" },
      },
    );
  let response = await read("omit");
  let viewer: Schemas["WhoAmIResponse"] | null = null;

  if (response.status === 404 && allowHiddenVersion) {
    const authorizingViewer = await getActualViewer({ signal });

    if (!authorizingViewer) throw notFound();

    response = await read("same-origin");
    viewer = authorizingViewer;
  }

  if (response.status === 404) throw notFound();

  const resolution = await parseResponse<PolicyVersionResolution>(response);
  return {
    selectedVersion: policyIdentity({ version: resolution.selected_version }),
    latestVersion: policyIdentity({ version: resolution.latest_version }),
    viewer,
  };
}
